import { NavLink } from 'react-router-dom';
import { LayoutGrid, Map, Bell, Bookmark, Calculator, FileBarChart2, FlaskConical, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '$lib/lib/cn';
import { useSavedSearches } from '$lib/hooks/useSavedSearches';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  // Show muted with "M5" tag for views that exist as routes but aren't built yet.
  pending?: 'M4' | 'M5';
  end?: boolean;
  badge?: number;
}

export function NavRail() {
  const savedCount = useSavedSearches().length;
  const { t } = useTranslation();
  const items: NavItem[] = [
    { to: '/units', label: t('nav.units'), icon: <LayoutGrid size={16} />, end: false },
    { to: '/estimate', label: t('nav.estimate'), icon: <Calculator size={16} /> },
    { to: '/map', label: t('nav.map'), icon: <Map size={16} /> },
    { to: '/signals', label: t('nav.signals'), icon: <Bell size={16} />, pending: 'M5' },
    { to: '/saved', label: t('nav.saved'), icon: <Bookmark size={16} />, badge: savedCount || undefined },
    { to: '/report', label: t('nav.report'), icon: <FileBarChart2 size={16} /> },
    { to: '/dev/components', label: t('nav.components'), icon: <FlaskConical size={16} /> },
    { to: '/admin', label: t('nav.admin'), icon: <Settings size={16} /> }
  ];
  return (
    <nav
      data-testid="nav-rail"
      aria-label={t('nav.primary')}
      className="flex w-14 shrink-0 flex-col items-stretch gap-0.5 border-r border-rule bg-surface py-2"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'group relative grid h-12 w-14 place-items-center text-fg-muted transition-colors duration-fast',
              'hover:bg-surface-2 hover:text-fg',
              isActive && 'text-accent'
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span aria-hidden className="absolute inset-y-1 left-0 w-0.5 rounded-r bg-accent" />
              )}
              <span className="flex flex-col items-center gap-0.5">
                {item.icon}
                <span className="text-[10px] leading-none">{item.label}</span>
              </span>
              {item.pending && (
                <span
                  aria-label={t('nav.comingInMilestone', { milestone: item.pending })}
                  className="absolute right-1 top-1 rounded-xs bg-surface-3 px-1 text-[9px] text-fg-subtle"
                >
                  {item.pending}
                </span>
              )}
              {item.badge ? (
                <span
                  aria-label={t('nav.savedCount', { count: item.badge })}
                  className="num absolute right-1 top-1 rounded-xs bg-accent px-1 text-[9px] text-accent-fg"
                >
                  {item.badge}
                </span>
              ) : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
